/**
 * Simple nth-point downsampling for performance.
 * @param data Array of objects (data points)
 * @param targetPoints Target number of points to keep
 * @returns Downsampled array
 */
export function downsample<T>(data: T[], targetPoints: number = 1000): T[] {
    if (!data || targetPoints < 2 || data.length <= targetPoints) {
        return data;
    }

    const ratio = Math.ceil(data.length / targetPoints);
    const result: T[] = [];

    for (let i = 0; i < data.length; i += ratio) {
        result.push(data[i]);
    }

    // Always keep the last point to show the "latest" value accurately
    if (result[result.length - 1] !== data[data.length - 1]) {
        result.push(data[data.length - 1]);
    }

    return result;
}

/**
 * Min-Max bucketed downsampling.
 * Better for preserving peaks/valleys than nth-point.
 * @param data Array of objects
 * @param targetPoints Target number of points (approximate)
 * @param valueKey The key of the numeric value in each object
 * @returns Downsampled array
 */
export function downsampleMinMax<T>(data: T[], targetPoints: number = 1000, valueKey: keyof T = "value" as any): T[] {
    if (!data || targetPoints < 2 || data.length <= targetPoints) {
        return data;
    }

    const bucketSize = Math.max(1, Math.floor(data.length / Math.max(1, targetPoints / 2)));
    const result: T[] = [];

    // Always keep the first point
    result.push(data[0]);

    for (let i = 1; i < data.length - 1; i += bucketSize) {
        let minIdx = -1;
        let maxIdx = -1;
        let minVal = Infinity;
        let maxVal = -Infinity;

        const end = Math.min(i + bucketSize, data.length - 1);
        for (let j = i; j < end; j++) {
            const raw = (data[j] as any)[valueKey];
            if (raw == null) continue;
            
            const val = Number(raw);
            if (isNaN(val)) continue;

            if (val < minVal) {
                minVal = val;
                minIdx = j;
            }
            if (val > maxVal) {
                maxVal = val;
                maxIdx = j;
            }
        }

        // Add min and max in chronological order
        if (minIdx !== -1 && maxIdx !== -1) {
            if (minIdx < maxIdx) {
                result.push(data[minIdx]);
                result.push(data[maxIdx]);
            } else if (minIdx > maxIdx) {
                result.push(data[maxIdx]);
                result.push(data[minIdx]);
            } else {
                result.push(data[minIdx]);
            }
        } else if (minIdx === -1 && i < data.length - 1) {
            // If no numeric values in bucket, just take the middle point to maintain continuity
            result.push(data[i + Math.floor((end - i) / 2)]);
        }
    }

    // Always keep the last point
    result.push(data[data.length - 1]);

    return result;
}
/**
 * Combined downsampling that preserves specific markers (e.g. trade entries/exits).
 * @param data Array of objects
 * @param targetPoints Target number of points
 * @param valueKey Numeric key for min-max
 * @param markerKeys Array of keys that, if non-null, force the point to be kept
 */
export function downsampleWithMarkers<T>(
    data: T[], 
    targetPoints: number = 1000, 
    valueKey: keyof T = "value" as any,
    markerKeys: (keyof T)[] = []
): T[] {
    if (!data || targetPoints < 2 || data.length <= targetPoints) {
        return data;
    }

    // 1. Identify all "must keep" indices
    const keepIndices = new Set<number>();
    keepIndices.add(0);
    keepIndices.add(data.length - 1);

    for (let i = 0; i < data.length; i++) {
        for (const key of markerKeys) {
            if (data[i][key] != null) {
                keepIndices.add(i);
                break;
            }
        }
    }

    // 2. Perform min-max downsampling on the rest
    // We'll just use the standard downsampleMinMax and then merge/sort
    // But a more efficient way is to just push markers into the result then sort.
    const downsampled = downsampleMinMax(data, targetPoints, valueKey);
    const resultSet = new Set(downsampled);
    
    for (const idx of keepIndices) {
        resultSet.add(data[idx]);
    }

    // Map items to their original indices for O(1) lookup during sort
    const indexMap = new Map<T, number>();
    for (let i = 0; i < data.length; i++) {
        indexMap.set(data[i], i);
    }

    // Sort by original index order
    return Array.from(resultSet).sort((a, b) => {
        return (indexMap.get(a) ?? 0) - (indexMap.get(b) ?? 0);
    });
}
